/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.kiloparsec

import kotlinx.serialization.Serializable
import net.sergeych.bintools.toDataSource
import net.sergeych.bipack.BipackDecoder
import net.sergeych.bipack.Unsigned
import net.sergeych.crypto2.*
import net.sergeych.synctools.ProtectedOp
import net.sergeych.synctools.invoke
import net.sergeych.utools.pack
import kotlin.math.roundToInt

/**
 * Parameters used in secured local and remote interfaces, etc. Actual values
 * are calculated in [KiloServerConnection] and [KiloClientConnection] by
 * exchanging keys, then used to encrypt/decrypt calls and results and create
 * [KiloScope] with a proper session object.
 *
 * __important: parameters' calculation algorithms are different on the server and
 * client side, so you need to provide proper [isServer] value!__
 */
data class KiloParams<S>(
    val isServer: Boolean,
    val transport: RemoteInterface,
    val sessionKey: SafeKeyExchange.SessionKey,
    val scopeSession: S,
    val remoteIdentity: VerifyingPublicKey?,
    val remoteTransport: RemoteInterface,
) {
    @Serializable
    data class Package(
        @Unsigned
        val nonce: ULong,
        val encryptedMessage: UByteArray,
    )

    private var nonce = 0UL

    val scope: KiloScope<S> by lazy {
        object : KiloScope<S> {
            override val session = scopeSession
            override val remote: RemoteInterface = remoteTransport
            override val sessionToken: UByteArray = token
            override val remoteIdentity: VerifyingPublicKey? = this@KiloParams.remoteIdentity
        }
    }

    val token: UByteArray by lazy {
        blake2b("token_".encodeToUByteArray() + sessionKey.sessionTag).sliceArray(0..<SymmetricKey.nonceLength)
    }

    private val numericNonce = NumericNonce(token)

    private val protectedOp = ProtectedOp()

    /**
     * Encrypt using send keys and proper nonce
     */
    fun encrypt(message: UByteArray, fillFactor: Float = 0f): UByteArray {
        val fill = if (fillFactor > 0f)
            0..(message.size * fillFactor).roundToInt()
        else
            null

        val n = protectedOp.invoke { nonce++ }

        return pack(
            Package(n, sessionKey.encryptWithNonce(message, numericNonce.withULong(n), fill))
        )
    }

    //    fun decryptString(cipherText: UByteArray): String = decrypt(cipherText).decodeFromUByteArray()
    fun decrypt(encryptedMessage: UByteArray): UByteArray =
        protectDecryption {
            val p: Package = BipackDecoder.decode(encryptedMessage.toDataSource())
            sessionKey.decryptWithNonce(p.encryptedMessage, numericNonce.withULong(p.nonce))
        }

}